import { CoordinatorService } from './coordinator.service';
import { UserValues } from './user-values';
import { Comment } from './comment';
import { CloudTeamManager } from './cloud-team-manager';
import { CloudChannel } from './cloud-channel';
import { UserTag } from './user-tag';

export type MANIFEST_UPDATE = {
    projectId: string;
    filename: string;
    hash: string;
};

export class User {
    id: string;
    // sid?: string
    name: string;
    email: string;
    receiveNotifications: boolean;
    isCurrent = false;
    isAdmin = false;

    isActive = true;

    expandedIdSet = new Set<string>();

    collectionDataLs: any;

    private _cloudUserTagLs: UserTag[] = [];

    private _userValuesMap = new Map<string, UserValues>();
    private _userCommentsMap = new Map<string, Comment[]>();

    private _fileHandleLs: FileSystemFileHandle[] = [];

    private _contentMap = new Map<string, any>();

    // private _manifestUpdateMap = new Map<string, MANIFEST_UPDATE>()
    private _affiliationSet = new Set<string>();
    private _isNTKset = new Set<string>();

    // private _awaitsCacheSaveSet: Set<string> = new Set<string>()

    constructor(
        public coords: CoordinatorService,
        public params: any,
    ) {
        this.id = params.id || params.userId;
        this.name = params.name || params.email;
        this.email = params.email;
        this.isAdmin = params.isAdmin;

        // prettier-ignore
        this.receiveNotifications = params.receiveNotifications !== undefined ? 
            params.receiveNotifications : true;
        this.isCurrent = params.isCurrent || false;
        if (this.isCurrent && this.id !== '_unlogged') {
            if (this.coords.display$.targetSite('!referentka')) {
                this.coords.license$.get();
            }
            this.coords.firebase$
                .callFunction('onLogin', {})
                .then((x: any) => this._processLoginData(x.data));
            const u$ = this.coords.user$;
            this.coords.project$.listenCloudProjects(this.id);
            // this.coords.book$.listenCloudBooks(this.id)

            // u$.cloudAttachmentManager = new CloudAttachmentManager(this);
            u$.cloudTeamManager = new CloudTeamManager(this);

            // u$.cloudUpdateManager = new CloudUpdateManager(this)
            u$.cloudChannel = new CloudChannel(this);
            this._subscribeAffiliations();
            u$.initUserGroups(this);
        }
    }

    get icon() {
        return this.coords.icon('user');
    }

    get isExternalLogin() {
        const extId = localStorage.getItem('extId');
        return extId === this.id;
    }

    private _sortUserTagLs() {
        this._cloudUserTagLs.sort((a, b) => a.title.localeCompare(b.title));
    }

    private _processLoginData(params: any) {
        console.log('PROCESS LOGIN DATA', params);
        if (params && params.userTagLs) {
            // prettier-ignore
            this._cloudUserTagLs = params.userTagLs
                .map((x: any) => new UserTag(this.coords, this, x));
            this._sortUserTagLs();
            console.log('USER TAGS', this._cloudUserTagLs);
        }
    }

    private async _subscribeAffiliations() {
        const col = 'externalAffiliation';
        const uns = await this.coords.firebase$.firestore.listenQuery(
            col,
            [
                { field: 'userId', value: this.id },
                {
                    field: 'expires',
                    operator: '>',
                    value: this.coords.firebase$.firestore.timestamp,
                },
            ],
            (params) => {
                params.docChanges().forEach((itm: any) => {
                    const d = itm.doc.data();
                    this._affiliationSet.add(d.affiliation);
                    this._affiliationSet.add(d.affiliation?.split('@').pop());
                });
            },
        );
        uns && this.coords.user$.addQuerySubscription(col, uns);

        try {
            const resp: any = await this.coords.firebase$.httpFunction('ntkUls');
            this._isNTKset = new Set(resp.data);
        } catch (e) {
            console.error('NTK-ULS fetch failed', e);
        }
    }

    isAffiliated(tp: string): boolean {
        switch (tp) {
            case 'ntk':
                for (const aff of this._affiliationSet) {
                    if (this._isNTKset.has(aff)) return true;
                }
                break;
        }
        return false;
    }

    clearCloudData() {
        this._userValuesMap.clear();
        this._userCommentsMap.clear();
    }

    get displayName() {
        return this.name || this.email || this.id;
    }

    async findCloudUser(tag: string) {
        tag = tag.trim();
        const resp = await this.coords.firebase$.callFunction('findTagUser', {
            tag: tag,
        });
        if (resp === 'ERROR') return undefined;
        const uData = resp.data as any;
        if (!this.coords.user$.get(uData.userId))
            this.coords.user$.addUser(new User(this.coords, uData));
        return uData;
    }

    get userTagLs() {
        return this._cloudUserTagLs;
    }

    get btMap() {
        return this.coords.binaryTree$.btMap;
    }

    removeTag(tag: UserTag) {
        this._cloudUserTagLs.splice(this._cloudUserTagLs.indexOf(tag), 1);
        this.coords.display$.markForCheck();
    }

    async setCloudTag(tag: string, isPublic = false) {
        tag = tag.trim();
        if (!this.isCurrent || !tag) return;
        const resp = await this.coords.firebase$.callFunction('setUserTag', {
            tag: tag,
            isPublic: isPublic,
        });
        if (resp === 'ERROR') return false;
        if (resp.data) {
            const tag = new UserTag(this.coords, this, resp.data);
            this._cloudUserTagLs.push(tag);
            this._sortUserTagLs();
            return true;
        } else return false;
    }

    // async deleteCloudTag(tag: string) {
    //     tag = tag.trim()
    //     if (!this.isCurrent || !tag) return
    //     const resp = await this.coords
    //         .firebase$.callFunction("deleteUserTag", { tag: tag })
    //     if (resp === "ERROR") return false
    //     if (resp.data) {
    //         this._cloudUserTagLs = this._cloudUserTagLs.filter(x => x.title !== tag)
    //         return true
    //     } else
    //         return false

    // }

    async updateData(params: {
        name?: string;
        userId?: string;
        // email?: string,
        receiveNotifications?: boolean;
    }) {
        if (!this.isCurrent) return;
        params.userId = this.id;
        this.name = params.name || this.name;
        this.receiveNotifications =
            params.receiveNotifications || params.receiveNotifications === undefined;

        const resp = await this.coords.firebase$.callFunction('updateUser', {
            name: this.name,
            receiveNotifications: this.receiveNotifications,
        });
        this.coords.display$.markForCheck();
    }

    public get fileHandleLs(): FileSystemFileHandle[] {
        return this._fileHandleLs;
    }

    public set fileHandleLs(value: FileSystemFileHandle[]) {
        this._fileHandleLs = value;
        this._processFilehandles();
    }

    private async _processFilehandles() {
        for (let fH of this._fileHandleLs) {
            if (!fH) continue;
            try {
                const f = await fH.getFile();
                const data = await this.coords.filesystem$.readFile(f);
                this._contentMap.set(f.name, JSON.parse(data.payload));
            } catch (e) {
                console.error(e);
            }
        }
    }

    getUserValues(parentId: string) {
        return this._userValuesMap.get(parentId);
    }

    async setUserValues(parentId: string, params: any) {
        const uv = new UserValues(this, parentId, params);
        this._userValuesMap.set(uv.parentId, uv);
    }
}
